import React, { ReactNode, useRef } from 'react';
import { SWRConfig } from 'swr';
import { fetcher } from '../services/fetcher';
import { AxiosError } from 'axios';
import { useLoading } from './LoadingContext';
import { APP_CONFIG } from '../config/appConfig';

interface SWRConfigProviderProps {
  children: ReactNode;
}

// Create a wrapper fetcher that tracks loading
const createTrackedFetcher = (
  incrementLoading: () => void,
  decrementLoading: () => void,
  loadingKeysRef: React.MutableRefObject<Set<string>>
) => {
  return async (url: string) => {
    // Only increment if this URL is not already loading
    if (!loadingKeysRef.current.has(url)) {
      loadingKeysRef.current.add(url);
      incrementLoading();
    }
    try {
      const result = await fetcher(url);
      return result;
    } finally {
      // Only decrement if this URL was in the loading set
      if (loadingKeysRef.current.has(url)) {
        loadingKeysRef.current.delete(url);
        decrementLoading();
      }
    }
  };
};

export const SWRConfigProvider: React.FC<SWRConfigProviderProps> = ({ children }) => {
  const { incrementLoading, decrementLoading } = useLoading();
  const loadingKeysRef = useRef<Set<string>>(new Set());

  const trackedFetcher = React.useMemo(
    () => createTrackedFetcher(incrementLoading, decrementLoading, loadingKeysRef),
    [incrementLoading, decrementLoading]
  );

  return (
    <SWRConfig
      value={{
        fetcher: trackedFetcher,
        revalidateOnFocus: false,
        revalidateOnReconnect: true,
        shouldRetryOnError: true,
        errorRetryCount: APP_CONFIG.api.retryAttempts,
        errorRetryInterval: APP_CONFIG.api.retryDelay,
        dedupingInterval: APP_CONFIG.api.cacheDuration, // Dedupe requests within cache duration
        onError: (error: AxiosError) => {
          // Handle global errors (loading keys are cleared in fetcher finally)
          if (error.response?.status === 401) {
            // Unauthorized - token will be cleared by interceptor
            console.error('Unauthorized access');
          } else if (error.response?.status && error.response.status >= 500) {
            console.error('Server error:', error);
          }
        },
        onErrorRetry: (
          error: AxiosError,
          key: string,
          config: any,
          revalidate: (opts?: { retryCount?: number }) => void,
          { retryCount }: { retryCount: number }
        ) => {
          // Don't retry on 4xx errors
          if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
            return;
          }
          // Retry on other errors
          if (retryCount >= (config?.errorRetryCount || 3)) {
            return;
          }
          setTimeout(() => revalidate({ retryCount }), config?.errorRetryInterval || 5000);
        },
      }}
    >
      {children}
    </SWRConfig>
  );
};

